import React from 'react';
import '../../css/components/MarkdownToolbar.css';

/**
 * Панель инструментов для вставки markdown-разметки в textarea.
 *
 * Работает через ref на <textarea> — определяет позицию курсора/выделение
 * и обёртывает/вставляет разметку, после чего вызывает onChange с новым значением.
 *
 * @param {React.RefObject<HTMLTextAreaElement>} textareaRef — ref на textarea
 * @param {string} value — текущее значение textarea
 * @param {Function} onChange — колбэк (newValue: string) => void
 * @param {boolean} [disabled] — блокировка кнопок
 */
export default function MarkdownToolbar({ textareaRef, value, onChange, disabled = false }) {

    /**
     * Обёртывает выделенный фрагмент текста перед/после маркерами.
     * Если выделения нет — вставляет маркеры и помещает курсор между ними.
     */
    const wrapSelection = (before, after = before, placeholder = '') => {
        const ta = textareaRef.current;
        if (!ta) return;

        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const selected = value.slice(start, end) || placeholder;
        const newValue = value.slice(0, start) + before + selected + after + value.slice(end);

        onChange(newValue);

        // Восстанавливаем фокус и выделение после React-рендера
        requestAnimationFrame(() => {
            ta.focus();
            const cursorStart = start + before.length;
            ta.setSelectionRange(cursorStart, cursorStart + selected.length);
        });
    };

    /**
     * Вставляет текст в начале строки(ок) выделения.
     * Используется для заголовков и цитат.
     */
    const prefixLines = (prefix, placeholder = '') => {
        const ta = textareaRef.current;
        if (!ta) return;

        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const selected = value.slice(start, end) || placeholder;

        // Каждая строка получает префикс
        const lines = selected.split('\n');
        const prefixed = lines.map(l => prefix + l).join('\n');
        const newValue = value.slice(0, start) + prefixed + value.slice(end);

        onChange(newValue);

        requestAnimationFrame(() => {
            ta.focus();
            ta.setSelectionRange(start, start + prefixed.length);
        });
    };

    /**
     * Вставляет блок текста на новой строке (для разделителя, блочного кода).
     */
    const insertBlock = (block, placeholder = '') => {
        const ta = textareaRef.current;
        if (!ta) return;

        const start = ta.selectionStart;
        const selected = value.slice(start, ta.selectionEnd) || placeholder;

        // Добавляем перенос строки перед блоком, если не в начале строки
        const needsNewlineBefore = start > 0 && value[start - 1] !== '\n';
        const prefix = needsNewlineBefore ? '\n' : '';
        const newValue = value.slice(0, start) + prefix + block.replace('{text}', selected) + value.slice(ta.selectionEnd);

        onChange(newValue);

        requestAnimationFrame(() => {
            ta.focus();
            const pos = start + prefix.length + block.indexOf('{text}');
            ta.setSelectionRange(pos, pos + selected.length);
        });
    };

    const buttons = [
        { label: 'B', title: 'Жирный (*текст*)', action: () => wrapSelection('*', '*', 'текст') },
        { label: 'I', title: 'Курсив (_текст_)', action: () => wrapSelection('_', '_', 'текст') },
        { label: 'S', title: 'Зачёркнутый (~~текст~~)', action: () => wrapSelection('~~', '~~', 'текст') },
        { label: 'H1', title: 'Заголовок 1', action: () => prefixLines('# ', 'Заголовок') },
        { label: 'H2', title: 'Заголовок 2', action: () => prefixLines('## ', 'Заголовок') },
        { label: 'H3', title: 'Заголовок 3', action: () => prefixLines('### ', 'Заголовок') },
        { label: '"', title: 'Цитата ([quote]текст[/quote])', action: () => wrapSelection('[quote]', '[/quote]', 'Цитата') },
        { label: '< >', title: 'Код (`код`)', action: () => wrapSelection('`', '`', 'код') },
        { label: '—', title: 'Разделитель (---)', action: () => insertBlock('\n---\n') },
        { label: '🔗', title: 'Ссылка [текст](url)', action: () => wrapSelection('[', '](https://)', 'текст ссылки') },
    ];

    return (
        <div className="md-toolbar">
            {buttons.map((btn, i) => (
                <button
                    key={i}
                    type="button"
                    className="md-toolbar__btn"
                    title={btn.title}
                    disabled={disabled}
                    onClick={btn.action}
                >
                    {btn.label}
                </button>
            ))}
        </div>
    );
}
